import type { RebalanceMetrics } from "../../../api/src/rebalances/rebalances.service";

/**
 * A `RebalanceMetrics` with every key `null`, the console's copy of the api's
 * `nullRebalanceMetrics` (`rebalances.service.ts`). For an object the console
 * builds itself (the manual rebalance explain), which must carry every key a
 * response carries (APP-1652). Typed against `RebalanceMetrics`, so a key added
 * to or removed from it fails `tsc` here.
 */
/**
 * `true` when the read computed the metrics of `account`. Every reader that
 * computes metrics writes `riskAllocation` as an object
 * (`getRebalancesMetrics`, `combineRebalanceData`,
 * `combineRebalanceRowFromBlob`, `api/src/rebalances/rebalances.service.ts`;
 * `buildManualRebalance`), and a read with `metrics=false` sends it `null`.
 * `totalBalance` does not tell the two apart: it is `null` on a read with
 * metrics for an account with no balance row.
 */
export const hasRebalanceMetrics = (
  account: Pick<RebalanceMetrics, "riskAllocation"> | undefined,
): boolean => account?.riskAllocation != null;

/**
 * `RebalanceMetrics` as the console holds it. `cashSettings` is `undefined`
 * when the console built the object itself and never loaded the account's
 * cash settings (the manual rebalance builders set it after spreading
 * `nullRebalanceMetrics()`), and `null` only when the API says the account has
 * no cash setting.
 */
export type ConsoleRebalanceMetrics = Omit<RebalanceMetrics, "cashSettings"> & {
  cashSettings: RebalanceMetrics["cashSettings"] | undefined;
};

export const nullRebalanceMetrics = (): RebalanceMetrics => ({
  totalBalance: null,
  cashBalance: null,
  buyTotal: null,
  sellTotal: null,
  realizedGainLossSt: null,
  realizedGainLossLt: null,
  reason: null,
  reasonChange: null,
  numTrades: null,
  checkStatus: null,
  checkScore: null,
  checkCritical: null,
  checkError: null,
  checkWarning: null,
  checkInfo: null,
  auditMaxSeverity: null,
  lastTradeDate: null,
  postRebalanceHoldings: null,
  assetAllocation: null,
  riskAllocation: null,
  assignedUserId: null,
  serviceTier: null,
  customFields: null,
  cashSettings: null,
});
